/**
 * MoonOrb — purpose-built moon phase visualiser.
 *
 * Props:
 *   phase  — 0..1 (0 = new moon, 0.5 = full moon, 1 = new moon again)
 *   size   — diameter in px
 *
 * Renders an SVG circle with a crescent/gibbous illumination path using the
 * SVG arc technique: one side is always a semicircle; the other side is an
 * ellipse whose x-radius interpolates from –r (new/dark) to +r (full/lit).
 *
 * Colors: var(--moon-glow) for lit area, dark fill for shadow hemisphere.
 * Animation: sigilCoreBreathe (defined in MoonOrb.module.css).
 */

import styles from './MoonOrb.module.css';

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------

export interface MoonOrbProps {
  /** 0 = new moon (fully dark), 0.5 = full moon (fully lit), 1 = new moon again */
  phase: number;
  /** Diameter in px */
  size: number;
  className?: string;
}

// --------------------------------------------------------------------------
// Phase illumination path builder
//
// Strategy (standard SVG crescent technique):
//   - Background circle = shadow color
//   - Overlay path = lit area
//
// The lit area is bounded by:
//   - Always a right semicircle (x > cx) when waxing, left when waning
//   - The terminator: an ellipse arc whose rx maps phase→ellipse x-radius
//
// phase 0   → new moon   → fully dark  (rx=-r, sweep matches shadow)
// phase 0.5 → full moon  → fully lit   (rx=+r, both arcs same side)
// phase 0.25→ first qtr  → right half  (rx=0, straight edge)
// phase 0.75→ last qtr   → left half   (rx=0, straight edge)
//
// Normalised phase 0..1 → mapped to 0..2 half-cycles (waxing then waning):
//   0..0.5: waxing — lit area on right, terminator sweeps right→left→right
//   0.5..1: waning — lit area on left, terminator sweeps left→right→left
// --------------------------------------------------------------------------

function buildLitPath(phase: number, r: number, cx: number, cy: number): string {
  // Remap to [0, 1] within the half-cycle
  const waxing = phase <= 0.5;
  // t goes 0→1 (new→full) for waxing, 1→0 (full→new) for waning
  const t = waxing ? phase * 2 : (1 - phase) * 2;

  // Ellipse x-radius for terminator: -r (dark side) → 0 (quarter) → +r (full)
  // When t=0: terminatorRx=-r (hidden behind shadow), t=0.5: 0, t=1: +r
  const terminatorRx = r * (2 * t - 1);

  // For waxing: lit area is right hemisphere (rightward large arc) + terminator arc
  // For waning: lit area is left hemisphere + terminator arc
  // Sweep flag for the outer semicircle edge (always 1 = clockwise = right side for waxing)
  // Sweep flag for terminator: 0 for waxing (concave left), 1 for waning (concave right)

  if (waxing) {
    // Top of circle → bottom via right (large arc, cw)
    // Bottom → top via terminator ellipse (rx=terminatorRx, ccw when rx<0 = dark crescent, cw when rx>0 = gibbous)
    const terminatorSweep = terminatorRx >= 0 ? 0 : 1;
    const absRx = Math.abs(terminatorRx);
    return [
      `M ${cx} ${cy - r}`,
      `A ${r} ${r} 0 0 1 ${cx} ${cy + r}`,
      `A ${absRx} ${r} 0 0 ${terminatorSweep} ${cx} ${cy - r}`,
      'Z',
    ].join(' ');
  } else {
    // Left hemisphere: top → bottom via left (large arc, ccw)
    const terminatorSweep = terminatorRx >= 0 ? 1 : 0;
    const absRx = Math.abs(terminatorRx);
    return [
      `M ${cx} ${cy - r}`,
      `A ${r} ${r} 0 0 0 ${cx} ${cy + r}`,
      `A ${absRx} ${r} 0 0 ${terminatorSweep} ${cx} ${cy - r}`,
      'Z',
    ].join(' ');
  }
}

// --------------------------------------------------------------------------
// Component
// --------------------------------------------------------------------------

export function MoonOrb({ phase, size, className }: MoonOrbProps): JSX.Element {
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 2; // inset 2px so stroke doesn't clip

  // Clamp phase to [0, 1]
  const p = Math.max(0, Math.min(1, phase));

  // Build lit area path (empty string = new moon = fully dark)
  const isNew = p < 0.005 || p > 0.995;
  const isFull = p > 0.495 && p < 0.505;

  let litPath: string | null = null;
  if (isFull) {
    // Full circle lit — just fill the whole circle
    litPath = null; // handled via full-circle overlay
  } else if (!isNew) {
    litPath = buildLitPath(p, r, cx, cy);
  }

  return (
    <div
      data-testid="moon-orb"
      className={[styles.root, className ?? ''].filter(Boolean).join(' ')}
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className={styles.svg}
        aria-hidden="true"
      >
        <defs>
          <radialGradient id="moon-orb-shadow" cx="40%" cy="35%" r="65%">
            <stop offset="0%"   stopColor="#1a1230" />
            <stop offset="100%" stopColor="#0b0820" />
          </radialGradient>
          <radialGradient id="moon-orb-lit" cx="45%" cy="38%" r="60%">
            <stop offset="0%"   stopColor="#fffcf0" />
            <stop offset="50%"  stopColor="var(--moon-glow, #fff8d8)" />
            <stop offset="100%" stopColor="#e8d890" />
          </radialGradient>
          <clipPath id={`moon-orb-clip-${size}`}>
            <circle cx={cx} cy={cy} r={r} />
          </clipPath>
        </defs>

        {/* Shadow base circle */}
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="url(#moon-orb-shadow)"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth="0.5"
        />

        {/* Lit area */}
        {isFull && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="url(#moon-orb-lit)"
            clipPath={`url(#moon-orb-clip-${size})`}
          />
        )}
        {!isFull && !isNew && litPath && (
          <path
            d={litPath}
            fill="url(#moon-orb-lit)"
            clipPath={`url(#moon-orb-clip-${size})`}
          />
        )}

        {/* Subtle rim glow */}
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="rgba(255,248,216,0.18)"
          strokeWidth="1"
        />
      </svg>
    </div>
  );
}
